import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { validatePasswordMatch } from "units/auth/utils/validation";
import { showToast } from "toast/toastHelper";
import { useSelector } from "react-redux";
import { PulseLoader } from "react-spinners";
import Header from "units/dashboard/components/Header";
import UserSidebar from "./components/UserSidebar";
import Footer from "units/dashboard/components/Footer";
import { changePassword } from "units/auth/services/authService";
import {
  Eye, EyeOff, Lock, Shield, Key, CheckCircle2, AlertCircle,
  Sparkles, ArrowLeft, ShieldCheck, Info, Fingerprint, Zap,
} from "lucide-react";
import { useTranslation } from "react-i18next";

// ========================================
// PasswordField
// ========================================
const PasswordField = ({
  label, name, value, show, onToggle, onChange, placeholder, autoComplete, index, showLabel, hideLabel,
}) => (
  <div
    className="group"
    style={{ animation: `fadeInUp 0.5s ease-out ${index * 0.08}s both` }}
  >
    <label className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-slate-700 dark:text-teal-100 mb-2">
      <span className="w-6 h-6 rounded-lg bg-gradient-to-br from-amber-300/40 to-teal-400/20 border border-amber-300/50 flex items-center justify-center shadow-sm">
        <Lock className="w-3 h-3 text-amber-600 dark:text-amber-300" />
      </span>
      {label}
    </label>

    <div className="relative">
      <input
        name={name}
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="
          w-full text-sm font-medium
          text-slate-800 dark:text-white
          bg-white/60 dark:bg-white/5
          backdrop-blur-xl
          border border-teal-500/25 dark:border-teal-400/20
          rounded-2xl
          px-4 py-3.5 pl-12
          transition-all duration-300
          placeholder:text-slate-400 dark:placeholder:text-teal-100/40
          focus:outline-none
          focus:border-amber-300/70
          focus:bg-white/80 dark:focus:bg-white/10
          focus:ring-4 focus:ring-amber-300/25
          hover:border-amber-300/60
          shadow-[inset_0_1px_0_rgba(255,255,255,0.7),0_2px_8px_rgba(0,0,0,0.04)]
          dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_2px_8px_rgba(0,0,0,0.3)]
          caret-teal-500 dark:caret-teal-300
        "
      />

      <button
        type="button"
        onClick={onToggle}
        aria-label={show ? hideLabel : showLabel}
        className="
          absolute left-3 top-1/2 -translate-y-1/2
          p-2 rounded-xl
          text-slate-500 dark:text-teal-100/70
          hover:text-amber-600 dark:hover:text-amber-300
          hover:bg-amber-300/10
          backdrop-blur-sm
          transition-all duration-300
          hover:scale-110
          focus:outline-none
        "
      >
        {show ? <EyeOff size={17} /> : <Eye size={17} />}
      </button>
    </div>
  </div>
);

// ========================================
// StarField
// ========================================
const StarField = () => {
  const [stars, setStars] = useState([]);

  React.useEffect(() => {
    const generatedStars = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      top: Math.random() * 100,
      left: Math.random() * 100,
      size: Math.random() * 3 + 1,
      delay: Math.random() * 5,
      duration: Math.random() * 3 + 2,
      opacity: Math.random() * 0.7 + 0.3,
    }));
    setStars(generatedStars);
  }, []);

  return (
    <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-teal-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950" />

      <div className="absolute inset-0 opacity-50 bg-gradient-to-tr from-transparent via-amber-50/30 to-transparent dark:via-teal-900/10" />

      {/* دایره‌های رنگی */}
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-gradient-to-br from-teal-300/30 to-teal-500/15 dark:from-teal-600/20 dark:to-teal-800/10 rounded-full blur-3xl animate-float" />

      <div
        className="absolute -bottom-32 -left-32 w-96 h-96 bg-gradient-to-br from-amber-300/30 to-amber-500/15 dark:from-amber-600/15 dark:to-amber-800/5 rounded-full blur-3xl animate-float"
        style={{ animationDelay: "500ms" }}
      />

      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-teal-200/20 via-amber-100/20 to-teal-200/20 dark:from-teal-800/10 dark:via-teal-900/10 dark:to-teal-950/10 rounded-full blur-3xl animate-float"
        style={{ animationDelay: "300ms" }}
      />

      {/* ستاره‌ها */}
      {stars.map((star) => (
        <div
          key={star.id}
          className="absolute rounded-full bg-gradient-to-br from-amber-400 to-teal-400 dark:from-amber-300 dark:to-teal-300"
          style={{
            top: `${star.top}%`,
            left: `${star.left}%`,
            width: `${star.size}px`,
            height: `${star.size}px`,
            opacity: star.opacity,
            animation: `twinkle ${star.duration}s ease-in-out ${star.delay}s infinite`,
            boxShadow: `0 0 ${star.size * 3}px rgba(212, 175, 55, 0.6)`,
          }}
        />
      ))}
    </div>
  );
};

// ========================================
// کامپوننت اصلی
// ========================================
const ChangeUserPassword = () => {
  const { t, i18n } = useTranslation(["user"]);
  const isEnglish = i18n.language === "en";

  const [formData, setFormData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const accessToken = useSelector((state) => state.auth.accessToken);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    const passwordRegex =
      /^[\u0600-\u06FFa-zA-Z0-9۰-۹!@#$%^&*()_+=\-{}\[\]:;"'<>,.?/\\|~`]*$/;
    if (value === "" || passwordRegex.test(value)) {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      const validationError = validatePasswordMatch(
        formData.newPassword,
        formData.confirmNewPassword
      );
      if (validationError) {
        setError(validationError);
        setLoading(false);
        return;
      }

      const res = await changePassword({
        accessToken,
        oldPassword: formData.oldPassword,
        password: formData.newPassword,
        confirmPassword: formData.confirmNewPassword,
      });

      if (res?.ok) {
        setSuccessMessage(t("changeUserPassword.toasts.successMessage"));
        showToast(t("changeUserPassword.toasts.successToast"), "success");
        setFormData({
          oldPassword: "",
          newPassword: "",
          confirmNewPassword: "",
        });
      }
    } catch (err) {
      console.log(err);
      setError(t("changeUserPassword.errors.generic"));
    } finally {
      setLoading(false);
    }
  };

  /* ---------- رمز فعلی پر شده؟ ---------- */
  const hasOld = formData.oldPassword.length > 0;
  const hasNew = formData.newPassword.length > 0;
  const hasConfirm = formData.confirmNewPassword.length > 0;

  return (
    <div className="relative min-h-screen">
      <StarField />

      {/* ================= Header ================= */}
      <nav className="shrink-0 z-30 h-[70px] flex items-center border-b border-teal-500/20 dark:border-teal-400/15 bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl shadow-[0_4px_30px_-12px_rgba(15,118,110,0.35)] dark:shadow-[0_4px_30px_-12px_rgba(0,0,0,0.7)] print:hidden fixed top-0 right-0 left-0">
        <Header />
      </nav>

      {/* ================= Sidebar ================= */}
      <div className="vertical-menu rtl:right-0 ltr:left-0 fixed bottom-0 top-[70px] h-[calc(100vh-70px)] w-64 border-r border-teal-500/10 dark:border-teal-400/10 bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl print:hidden z-20 overflow-hidden">
        <UserSidebar />
      </div>

      {/* ================= Main ================= */}
      <div className="lg:rtl:pr-64 lg:ltr:pl-64 pt-[70px] min-h-screen flex flex-col">
        <div className="flex-1 px-3 sm:px-4 md:px-6 py-6 sm:py-10">
          <div className="max-w-4xl mx-auto">

            {/* ============ Page Header ============ */}
            <div className="mb-6 sm:mb-8" style={{ animation: "fadeInUp 0.6s ease-out" }}>
              <div className="flex items-center gap-3">
                <span className="shrink-0 relative flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-300 via-amber-400 to-amber-600 shadow-[0_8px_24px_-8px_rgba(251,191,36,0.8)] border border-amber-200/60">
                  <Shield className="h-6 w-6 text-white drop-shadow" />
                  <Sparkles className="absolute -top-1 -right-1 w-4 h-4 text-amber-100 animate-float" />
                </span>
                <div className="min-w-0">
                  <h1 className="text-xl sm:text-2xl font-extrabold bg-gradient-to-l from-teal-700 via-teal-600 to-amber-500 dark:from-amber-200 dark:via-teal-300 dark:to-teal-400 bg-clip-text text-transparent">
                    {t("changeUserPassword.pageTitle")}
                  </h1>
                  <p className="text-[11px] sm:text-sm text-slate-500 dark:text-teal-100/70 mt-1">
                    {t("changeUserPassword.pageSubtitle")}
                  </p>
                </div>
              </div>
            </div>

            {/* ============ Grid: Form + Tips ============ */}
            <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-6 lg:gap-8 items-start">

              {/* ================= FORM (Right) ================= */}
              <form
                onSubmit={handleSubmit}
                noValidate
                className="space-y-5"
                style={{ animation: "fadeInUp 0.6s ease-out 0.1s both" }}
              >
                <PasswordField
                  label={t("changeUserPassword.fields.oldLabel")}
                  name="oldPassword"
                  value={formData.oldPassword}
                  show={showOld}
                  onToggle={() => setShowOld((s) => !s)}
                  onChange={handleChange}
                  placeholder={t("changeUserPassword.fields.oldPlaceholder")}
                  autoComplete="current-password"
                  index={0}
                  showLabel={t("changeUserPassword.showPassword")}
                  hideLabel={t("changeUserPassword.hidePassword")}
                />

                <PasswordField
                  label={t("changeUserPassword.fields.newLabel")}
                  name="newPassword"
                  value={formData.newPassword}
                  show={showNew}
                  onToggle={() => setShowNew((s) => !s)}
                  onChange={handleChange}
                  placeholder={t("changeUserPassword.fields.newPlaceholder")}
                  autoComplete="new-password"
                  index={1}
                  showLabel={t("changeUserPassword.showPassword")}
                  hideLabel={t("changeUserPassword.hidePassword")}
                />

                <PasswordField
                  label={t("changeUserPassword.fields.confirmLabel")}
                  name="confirmNewPassword"
                  value={formData.confirmNewPassword}
                  show={showConfirm}
                  onToggle={() => setShowConfirm((s) => !s)}
                  onChange={handleChange}
                  placeholder={t("changeUserPassword.fields.confirmPlaceholder")}
                  autoComplete="new-password"
                  index={2}
                  showLabel={t("changeUserPassword.showPassword")}
                  hideLabel={t("changeUserPassword.hidePassword")}
                />

                {/* ---------- Progress dots: وضعیت تکمیل ---------- */}
                <div className="flex items-center justify-center gap-2 pt-1">
                  {[
                    { done: hasOld, label: t("changeUserPassword.steps.old") },
                    { done: hasNew, label: t("changeUserPassword.steps.new") },
                    { done: hasConfirm && hasNew && hasConfirm === hasNew, label: t("changeUserPassword.steps.confirm") },
                  ].map((step, i) => (
                    <React.Fragment key={i}>
                      <span
                        className={`
                          h-2 w-2 rounded-full transition-all duration-300
                          ${step.done
                            ? "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)] scale-125"
                            : "bg-slate-300 dark:bg-slate-700"
                          }
                        `}
                        title={step.label}
                      />
                      {i < 2 && (
                        <span
                          className={`
                            h-px w-10 transition-colors duration-300
                            ${step.done ? "bg-emerald-400/60" : "bg-slate-300 dark:bg-slate-700"}
                          `}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>

                {/* ---------- Error ---------- */}
                {error && (
                  <div
                    className="flex items-center gap-3 p-3.5 rounded-2xl bg-rose-500/10 dark:bg-rose-500/15 backdrop-blur-xl border border-rose-400/40 dark:border-rose-400/30 text-rose-700 dark:text-rose-200 text-xs sm:text-sm font-medium"
                    style={{ animation: "fadeInUp 0.3s ease-out" }}
                  >
                    <span className="shrink-0 w-8 h-8 rounded-xl bg-rose-500 flex items-center justify-center shadow-md">
                      <AlertCircle className="w-4 h-4 text-white" />
                    </span>
                    <span>{error}</span>
                  </div>
                )}

                {/* ---------- Success ---------- */}
                {successMessage && (
                  <div
                    className="flex items-center gap-3 p-3.5 rounded-2xl bg-emerald-500/10 dark:bg-emerald-500/15 backdrop-blur-xl border border-emerald-400/40 dark:border-emerald-400/30 text-emerald-700 dark:text-emerald-200 text-xs sm:text-sm font-medium"
                    style={{ animation: "fadeInUp 0.3s ease-out" }}
                  >
                    <span className="shrink-0 w-8 h-8 rounded-xl bg-emerald-500 flex items-center justify-center shadow-md">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </span>
                    <span>{successMessage}</span>
                  </div>
                )}

                {/* ---------- Submit ---------- */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="
                      group relative w-full overflow-hidden
                      py-3.5 px-6 rounded-2xl
                      text-sm sm:text-base font-bold text-white
                      bg-gradient-to-l from-teal-700 via-teal-600 to-teal-500
                      bg-[length:200%_100%] hover:bg-[position:100%_0]
                      shadow-[0_8px_24px_-8px_rgba(15,118,110,0.8)]
                      hover:shadow-[0_12px_32px_-8px_rgba(20,184,166,1)]
                      transition-all duration-500
                      hover:scale-[1.01] active:scale-[0.99]
                      disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100
                    "
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out" />

                    <div className="relative flex items-center justify-center gap-2.5">
                      {!loading ? (
                        <>
                          <Key className="w-4 h-4 group-hover:rotate-12 transition-transform duration-300" />
                          <span>{t("changeUserPassword.submit")}</span>
                          <ArrowLeft className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                        </>
                      ) : (
                        <>
                          <PulseLoader color="#ffffff" size={8} />
                          <span>{t("changeUserPassword.submitting")}</span>
                        </>
                      )}
                    </div>
                  </button>
                </div>
              </form>
            </div>

            {/* Footer */}
            <div className="mt-10">
              <Footer />
            </div>
          </div>
        </div>
      </div>

      {/* انیمیشن‌ها */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-10px); }
        }
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50%      { opacity: 1;   transform: scale(1.3); }
        }
        .animate-float { animation: float 3s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default ChangeUserPassword;